import { useState } from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { GoogleLogo } from "../components/GoogleLogo";
import { Spinner } from "../components/Loading";
import { useAuth } from "../lib/auth";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<"email" | "google" | null>(null);
  const notice = (location.state as { notice?: string } | null)?.notice;

  if (!auth.loading && auth.accessToken) return <Navigate to="/dashboard" replace />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    setLoading("email");
    try {
      await auth.login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : "Login failed");
    } finally {
      setLoading(null);
    }
  }

  async function googleLogin() {
    setError("");
    setLoading("google");
    try {
      await auth.googleLogin();
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : "Google login failed");
      setLoading(null);
    }
  }

  return (
    <AuthLayout subtitle="Parental Control Dashboard">
      <h2>Sign in to continue</h2>
      {notice ? <div className="alert alert-success">{notice}</div> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}
      <button
        type="button"
        className="btn btn-google"
        onClick={googleLogin}
        disabled={Boolean(loading)}
      >
        {loading === "google" ? <Spinner small /> : <GoogleLogo />}
        {loading === "google" ? "Redirecting to Google..." : "Continue with Google"}
      </button>
      <div className="auth-divider">or</div>
      <form className="form-stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            className="input-field"
            type="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            className="input-field"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>
        <div className="form-meta">
          <span />
          <Link className="text-link" to="/forgot-password">
            Forgot Password?
          </Link>
        </div>
        <button className="btn btn-primary btn-block" disabled={Boolean(loading)}>
          {loading === "email" ? <Spinner small /> : null}
          {loading === "email" ? "Signing in..." : "Sign In"}
        </button>
      </form>
      <p className="auth-switch">
        Don&apos;t have an account?{" "}
        <Link to="/signup" className="text-link">
          Sign Up
        </Link>
      </p>
      <p className="auth-note">Sign in with your existing LimitX Browser account.</p>
    </AuthLayout>
  );
}
